import { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { trackVideo } from '../../../utils/tracking';

type VideoOverlayProps = {
  videoId: string;
  videoTitle?: string;
  onClose: () => void;
};

export function VideoOverlay({ videoId, videoTitle = 'Unknown Video', onClose }: VideoOverlayProps) {
  const openTimeRef = useRef<number>(Date.now());

  const handleClose = useCallback(() => {
    const watchDuration = (Date.now() - openTimeRef.current) / 1000;
    trackVideo.overlayClose(videoId, videoTitle, watchDuration);
    onClose();
  }, [videoId, videoTitle, onClose]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

  return createPortal(
    <div className="video-overlay" onClick={handleBackdropClick}>
      <button className="video-overlay__close" onClick={handleClose} aria-label="Close video">
        <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </button>
      <div className="video-overlay__content">
        <iframe
          className="video-overlay__iframe"
          src={embedUrl}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    </div>,
    document.body
  );
}
